import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { DateTime } from "luxon";
import { z } from "zod";
import { firestore } from "../firebase";
import type { BookedTraining } from "../model/BookedTraining";
import type { Student } from "../providers/studentsProvider";
import type { Adas } from "../providers/adasProvider";
import { drivingSchoolsProvider } from "../providers/drivingSchoolsProvider";
import { studentDrivingLessonsProvider } from "../providers/studentDrivingLessonsProvider";
import { buildPracticalProgress, type PracticalProgress } from "../utils/practicalProgress";
import { PostalAddressSchema } from "../model/PostalAddress";
import { getGoogleMapsUrl } from "../utils/getGoogleMapsUrl";
import { getAuthenticatedServerClient } from "./server.api";

const PointOfInterestSchema = z.object({
  name: z.string(),
  description: z.string().nullish(),
  examLocations: z.array(z.string()).nullish(),
  address: PostalAddressSchema.nullish(),
});

export async function getStudentPracticalProgress(
  student: Pick<Student, "id" | "practicalExamLocationUid">,
  training: BookedTraining,
): Promise<PracticalProgress> {
  const [template, progress, pointsOfInterest, adas] = await Promise.all([
    getDoc(doc(firestore, "license_progress_templates", training.drivingLicenseClass)),
    getDoc(doc(firestore, "users", student.id, "license_progress", training.drivingLicenseClass)),
    getDocs(
      query(
        collection(firestore, "driving_schools", training.drivingSchoolId, "points_of_interest"),
        where("deleted", "==", false),
      ),
    ),
    getUsedAdas(student.id, training),
  ]);
  if (!template.exists()) {
    throw new Error(`No license progress template found for ${training.drivingLicenseClass}`);
  }
  const pois = pointsOfInterest.docs
    .map((snapshot) => ({ id: snapshot.id, ...PointOfInterestSchema.parse(snapshot.data()) }))
    .filter((poi) => poi.examLocations?.includes(student.practicalExamLocationUid ?? ""))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    .map((poi) => ({
      id: poi.id,
      title: poi.name,
      description: poi.description ?? "",
      link: getGoogleMapsUrl(poi.address),
      linkLabel: "In Karte öffnen",
    }));
  return buildPracticalProgress(template.data(), progress.data(), {
    adas: adas.map((entry) => ({ id: entry.id, title: entry.name })),
    pois,
    hasExamLocation: !!student.practicalExamLocationUid,
  });
}

async function getUsedAdas(studentId: string, training: BookedTraining): Promise<Adas[]> {
  const counts = training.kpis?.finishedDrivingLessonsCount;
  const finishedLessons =
    (counts?.normal ?? 0) + (counts?.ueberlandfahrt ?? 0) + (counts?.autobahnfahrt ?? 0) + (counts?.nachtfahrt ?? 0);
  if (finishedLessons === 0) {
    return [];
  }
  const [drivingSchool, lessons, availableAdas] = await Promise.all([
    drivingSchoolsProvider.getOne("drivingSchools", { id: training.drivingSchoolId }),
    studentDrivingLessonsProvider.getList("studentDrivingLessons", {
      filter: { studentUid: studentId, bookedTrainingIds: [training.id] },
      pagination: { page: 1, perPage: 0 },
      sort: { field: "start", order: "ASC" },
    }),
    getAuthenticatedServerClient().then((client) => client.listAdas()),
  ]);
  const now = DateTime.now();
  const vehicleIds = new Set(
    lessons.data
      .filter(
        (lesson) =>
          !lesson.deleted &&
          lesson.drivingSchoolId === training.drivingSchoolId &&
          lesson.student.uid === studentId &&
          lesson.student.bookedTrainingId === training.id &&
          lesson.drivingLessonType !== "simulator" &&
          lesson.endedAt &&
          (lesson.student.rsvp === "accepted" ||
            lesson.student.rsvp === "no-show" ||
            (lesson.student.rsvp === "pending" && lesson.end > now)),
      )
      .map((lesson) => lesson.vehicleId)
      .filter((id) => id && id !== "__own_motorcycle__"),
  );
  const adasIds = new Set(
    drivingSchool.data.vehicles.filter((vehicle) => vehicleIds.has(vehicle.id)).flatMap((vehicle) => vehicle.adasIds),
  );
  return availableAdas.filter((adas) => adasIds.has(adas.id));
}
